import React, { createContext, useContext, useEffect, useReducer, useState, useCallback, useRef } from 'react';

const JobContext = createContext(null);

const initialState = {
  activeJobs: {},    // { [job_id]: { query, status, progress, progress_percent, started_at } }
  queuedJobs: {},    // { [job_id]: { query, queued_at } }
  completedJobs: [], // [{ job_id, query, result_count, completed_at }]
  failedJob: null,   // { job_id, query, error }
  leadOps: {},       // { [company_name]: { status, operation_id } }
};

function reducer(state, action) {
  switch (action.type) {
    case 'JOB_QUEUED':
      return {
        ...state,
        queuedJobs: { ...state.queuedJobs, [action.job_id]: { query: action.query, display_name: action.display_name || '', queued_at: action.queued_at } },
      };
    case 'JOB_STARTED':
      const { [action.job_id]: removed, ...restQueued } = state.queuedJobs;
      return {
        ...state,
        queuedJobs: restQueued,
        activeJobs: { ...state.activeJobs, [action.job_id]: { query: action.query, display_name: action.display_name || '', status: 'running', progress: action.progress || '', progress_percent: action.progress_percent || 0, started_at: action.started_at } },
      };
    case 'JOB_PROGRESS':
      return {
        ...state,
        activeJobs: {
          ...state.activeJobs,
          [action.job_id]: { ...state.activeJobs[action.job_id], progress: action.progress, progress_percent: action.progress_percent },
        },
      };
    case 'JOB_COMPLETED': {
      const { [action.job_id]: completedJob, ...restActive } = state.activeJobs;
      const newCompleted = {
        job_id: action.job_id,
        query: action.query || completedJob?.query || '',
        result_count: action.result_count || 0,
        leads: action.leads || [],
        stages: action.stages || {},
        completed_at: new Date().toISOString()
      };
      return {
        ...state,
        activeJobs: restActive,
        completedJobs: [newCompleted, ...state.completedJobs].slice(0, 20),
      };
    }
    case 'JOB_FAILED': {
      const { [action.job_id]: failedJob, ...restActiveFailed } = state.activeJobs;
      // Auto-dismiss failed job banner after 10 seconds
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('ileadx:dismiss-failed', { detail: action.job_id }));
      }, 10000);
      return {
        ...state,
        activeJobs: restActiveFailed,
        failedJob: { job_id: action.job_id, query: action.query || failedJob?.query || '', error: action.error },
      };
    }
    case 'LEAD_PROCESSING':
      return {
        ...state,
        leadOps: { ...state.leadOps, [action.company_name]: { status: 'processing', operation_id: action.operation_id } },
      };
    case 'LEAD_UPDATED':
      return {
        ...state,
        leadOps: { ...state.leadOps, [action.company_name]: { status: action.status, operation_id: action.operation_id } },
      };
    case 'DISMISS_COMPLETED':
      return {
        ...state,
        completedJobs: state.completedJobs.filter(j => j.job_id !== action.job_id),
      };
    case 'DISMISS_FAILED':
      return { ...state, failedJob: null };
    default:
      return state;
  }
}

export function JobProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [leadUpdateEvent, setLeadUpdateEvent] = useState(null);
  const [wsConnected, setWsConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const userId = localStorage.getItem('user_id');

  const getApiBase = () => {
    const host = window.location.hostname;
    const isDev = host === 'localhost' || host === '127.0.0.1' || host.startsWith('192.168.') || host.startsWith('10.') || host.startsWith('172.');
    if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
    return isDev ? `http://${host}:8000` : 'https://api.ileadx.io';
  };

  
  const connect = useCallback(() => {
    if (!userId) return;

    // Listen for auto-dismiss events
    const handleDismissFailed = (e) => dispatch({ type: 'DISMISS_FAILED' });
    window.addEventListener('ileadx:dismiss-failed', handleDismissFailed);

    // Fetch existing jobs IMMEDIATELY (don't wait for WebSocket)
    const fetchExisting = async () => {
      try {
        const token = localStorage.getItem('access_token');
        if (!token) return;
        const api = (await import('../lib/api')).default;
        const res = await api.get('/api/search/status', { params: { _t: Date.now() } });
        const data = res.data;
        if (data.active_jobs) {
          data.active_jobs.forEach(j => dispatch({ type: 'JOB_STARTED', job_id: j.job_id, query: j.query, progress: j.progress, progress_percent: j.progress_percent, started_at: j.started_at }));
        }
        if (data.queued_jobs) {
          data.queued_jobs.forEach(j => dispatch({ type: 'JOB_QUEUED', job_id: j.job_id, query: j.query, queued_at: j.queued_at }));
        }
        if (data.completed_job) {
          dispatch({ type: 'JOB_COMPLETED', job_id: data.completed_job.job_id, query: data.completed_job.query, result_count: data.completed_job.leads?.length || 0 });
        }
      } catch (e) {}
    };

    // Fetch existing jobs immediately before WebSocket connects
    fetchExisting();
    
    const wsBase = getApiBase().replace(/^http/, 'ws');
    const token = localStorage.getItem('access_token');
    const ws = new WebSocket(`${wsBase}/api/notifications/ws/${userId}?source=job_updates${token ? `&token=${encodeURIComponent(token)}` : ''}`);

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        switch (data.type) {
          case 'job_queued':
            dispatch({ type: 'JOB_QUEUED', job_id: data.job_id, query: data.query, queued_at: data.queued_at });
            break;
          case 'job_started':
            dispatch({ type: 'JOB_STARTED', job_id: data.job_id, query: data.query, progress: data.progress, progress_percent: data.progress_percent, started_at: data.started_at });
            break;
          case 'job_progress':
            dispatch({ type: 'JOB_PROGRESS', job_id: data.job_id, progress: data.progress, progress_percent: data.progress_percent });
            break;
          case 'job_completed':
            dispatch({ type: 'JOB_COMPLETED', job_id: data.job_id, query: data.query, result_count: data.result_count, leads: data.leads || [], stages: data.stages || {} });
            break;
          case 'job_failed':
            dispatch({ type: 'JOB_FAILED', job_id: data.job_id, query: data.query, error: data.error });
            break;
          case 'lead_processing_started':
            dispatch({ type: 'LEAD_PROCESSING', company_name: data.company_name, operation_id: data.operation_id });
            break;
          case 'lead_updated':
            dispatch({ type: 'LEAD_UPDATED', company_name: data.company_name, operation_id: data.operation_id, status: data.status, lead: data.lead });
            setLeadUpdateEvent({ company_name: data.company_name, lead: data.lead, timestamp: Date.now() });
            break;
          default:
            break;
        }
      } catch {}
    };
    let reconnectDelay = 500; // Start at 500ms, not 3000ms
    
    ws.onclose = () => {
      console.log('📡 Job WebSocket disconnected, reconnecting in ' + reconnectDelay + 'ms');
      setWsConnected(false);
      reconnectTimeoutRef.current = setTimeout(() => {
        connect();
        reconnectDelay = Math.min(reconnectDelay * 2, 10000); // Exponential backoff: 0.5s → 1s → 2s → 4s → 8s → 10s max
      }, reconnectDelay);
    };
    ws.onerror = () => {
      console.log('📡 Job WebSocket error, closing');
      ws.close();
    };
    
    // Reset reconnect delay on successful connection
    ws.onopen = () => {
      console.log('📡 Job WebSocket connected');
      setWsConnected(true);
      reconnectDelay = 500;
      // Refresh active/queued jobs after connection to catch any missed events
      fetchExisting();
    };
    
    // Heartbeat: send ping every 25 seconds to detect dead connections
    const heartbeatInterval = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ action: 'ping' }));
      }
    }, 25000);
    
    wsRef.current = ws;
    // Store heartbeat interval on the ref for cleanup
    wsRef._heartbeat = heartbeatInterval;

    return () => {
      clearTimeout(reconnectTimeoutRef.current);
      if (wsRef._heartbeat) clearInterval(wsRef._heartbeat);
      ws.close();
    };
  }, [userId]);

  useEffect(() => {
    const cleanup = connect();
    return () => cleanup && cleanup();
  }, [connect]);

  // Conditional polling: only when there are active/queued jobs
  useEffect(() => {
    const hasActive = Object.keys(state.activeJobs).length > 0 || Object.keys(state.queuedJobs).length > 0;
    if (!hasActive) return;

    const poll = async () => {
      try {
        const token = localStorage.getItem('access_token');
        if (!token) return;
        const api = (await import('../lib/api')).default;
        const res = await api.get('/api/search/status', { params: { _t: Date.now() } });
        const data = res.data;
        if (data.active_jobs) {
          data.active_jobs.forEach(j => dispatch({ type: 'JOB_STARTED', job_id: j.job_id, query: j.query, progress: j.progress, progress_percent: j.progress_percent, started_at: j.started_at }));
        }
        if (data.queued_jobs) {
          data.queued_jobs.forEach(j => dispatch({ type: 'JOB_QUEUED', job_id: j.job_id, query: j.query, queued_at: j.queued_at }));
        }
        if (data.completed_job) {
          dispatch({ type: 'JOB_COMPLETED', job_id: data.completed_job.job_id, query: data.completed_job.query, result_count: data.completed_job.leads?.length || 0, leads: data.completed_job.leads || [], stages: data.completed_job.stages || {} });
        }
      } catch {}
    };

    poll();
    const interval = setInterval(poll, 3000);
    return () => clearInterval(interval);
  }, [state.activeJobs, state.queuedJobs]);

  // Fallback polling when WebSocket is disconnected (and jobs exist)
  useEffect(() => {
    if (wsConnected) return;
    const hasActive = Object.keys(state.activeJobs).length > 0 || Object.keys(state.queuedJobs).length > 0;
    if (!hasActive) return;

    const fallbackPoll = async () => {
      try {
        const token = localStorage.getItem('access_token');
        if (!token) return;
        const api = (await import('../lib/api')).default;
        const res = await api.get('/api/search/status', { params: { _t: Date.now() } });
        const data = res.data;
        if (data.active_jobs) {
          data.active_jobs.forEach(j => dispatch({ type: 'JOB_STARTED', job_id: j.job_id, query: j.query, progress: j.progress, progress_percent: j.progress_percent, started_at: j.started_at }));
        }
        if (data.queued_jobs) {
          data.queued_jobs.forEach(j => dispatch({ type: 'JOB_QUEUED', job_id: j.job_id, query: j.query, queued_at: j.queued_at }));
        }
        if (data.completed_job) {
          dispatch({ type: 'JOB_COMPLETED', job_id: data.completed_job.job_id, query: data.completed_job.query, result_count: data.completed_job.leads?.length || 0, leads: data.completed_job.leads || [], stages: data.completed_job.stages || {} });
        }
      } catch {}
    };

    fallbackPoll();
    const interval = setInterval(fallbackPoll, 3000);
    return () => clearInterval(interval);
  }, [wsConnected, state.activeJobs, state.queuedJobs]);

  const dismissCompleted = useCallback((job_id) => dispatch({ type: 'DISMISS_COMPLETED', job_id }), []);
  const dismissFailed = useCallback(() => dispatch({ type: 'DISMISS_FAILED' }), []);

  // Manual single refresh (used on page mount to catch missed jobs)
  const refreshJobs = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) return;
      const api = (await import('../lib/api')).default;
      const res = await api.get('/api/search/status', { params: { _t: Date.now() } });
      const data = res.data;
      if (data.active_jobs) {
        data.active_jobs.forEach(j => dispatch({ type: 'JOB_STARTED', job_id: j.job_id, query: j.query, progress: j.progress, progress_percent: j.progress_percent, started_at: j.started_at }));
      }
      if (data.queued_jobs) {
        data.queued_jobs.forEach(j => dispatch({ type: 'JOB_QUEUED', job_id: j.job_id, query: j.query, queued_at: j.queued_at }));
      }
      if (data.completed_job) {
        dispatch({ type: 'JOB_COMPLETED', job_id: data.completed_job.job_id, query: data.completed_job.query, result_count: data.completed_job.leads?.length || 0, leads: data.completed_job.leads || [], stages: data.completed_job.stages || {} });
      }
    } catch {}
  }, []);

  return (
    <JobContext.Provider value={{ state, dispatch, dismissCompleted, dismissFailed, refreshJobs }}>
      {children}
    </JobContext.Provider>
  );
}

export function useJobContext() {
  const ctx = useContext(JobContext);
  if (!ctx) throw new Error('useJobContext must be used inside JobProvider');
  return ctx;
}
